import React, { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import { EinvoiceAccessBadge } from '../components/EinvoiceGate';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  Building2, Users, FileText, Plus, UserPlus, 
  ToggleLeft, ToggleRight, Key, Shield, TrendingUp,
  Database, Download, Upload, AlertTriangle, Trash2, Power, PowerOff, UserCog, FileCheck2, Truck,
  CalendarClock, Dices, Copy, Info
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Forms
  const [companyForm, setCompanyForm] = useState({ name: '', username: '', password: '', email: '', piva: '', address: '', city: '', cap: '', phone: '' });
  const [userForm, setUserForm] = useState({ email: '', full_name: '', password: '', company_id: '', role: 'company_user' });
  const [newPassword, setNewPassword] = useState('');
  const [backups, setBackups] = useState([]);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreConfirm, setRestoreConfirm] = useState('');
  
  // Company actions dialogs
  const [companyPasswordDialog, setCompanyPasswordDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyNewPassword, setCompanyNewPassword] = useState('');
  const [companyUsernameDialog, setCompanyUsernameDialog] = useState(false);
  const [companyNewUsername, setCompanyNewUsername] = useState('');
  const [deleteCompanyDialog, setDeleteCompanyDialog] = useState(false);
  const [deleteCompanyConfirm, setDeleteCompanyConfirm] = useState('');
  const [myPasswordDialog, setMyPasswordDialog] = useState(false);
  const [myNewPassword, setMyNewPassword] = useState('');
  // Slave (operatore) credentials dialog
  const [slaveDialog, setSlaveDialog] = useState(false);
  const [slaveUsername, setSlaveUsername] = useState('');
  const [slavePassword, setSlavePassword] = useState('');
  // Subscription renew dialog
  const [renewDialog, setRenewDialog] = useState(false);
  const [renewDays, setRenewDays] = useState(365);
  const [renewFromToday, setRenewFromToday] = useState(false);
  // Reveal-generated-password dialog (one-shot)
  const [revealDialog, setRevealDialog] = useState(false);
  const [revealedPwd, setRevealedPwd] = useState(null); // { username, target, temporary_password, company_name }
  const [randomLoading, setRandomLoading] = useState(false);
  
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, companiesRes, usersRes] = await Promise.all([
        adminApi.getStats(),
        adminApi.getCompanies(),
        adminApi.getUsers()
      ]);
      setStats(statsRes.data);
      setCompanies(companiesRes.data);
      setUsers(usersRes.data);
      fetchBackups();
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const fetchBackups = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${BACKEND_URL}/api/admin/backup/list`, { headers: { Authorization: `Bearer ${token}` } });
      setBackups(r.data || []);
    } catch (e) {
      // Lista backup non critica: logga solo in dev, non bloccare la UI.
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[Admin] fetchBackups failed:', e?.response?.status || e?.message);
      }
    }
  };

  const handleDownloadBackup = async () => {
    // Feedback immediato all'utente: il pulsante è stato premuto
    const loadingToastId = toast.loading('Preparazione backup in corso...');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.dismiss(loadingToastId);
        toast.error('Sessione scaduta — rifai login');
        return;
      }
      const r = await axios.get(`${BACKEND_URL}/api/admin/backup/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 300000, // 5 minuti per backup molto grandi (con compressione GZip ridotta drasticamente)
      });
      // Verifica che il blob sia valido
      if (!r.data || r.data.size === 0) {
        throw new Error('Backup vuoto ricevuto dal server');
      }
      const blob = new Blob([r.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const fileName = `ddtmanager_backup_${new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-')}.json`;

      // Tentativo download standard (Chrome, Edge, Firefox desktop)
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.style.display = 'none';
      // Fix Safari/iPad: link must be in DOM
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Fallback per Safari iPad/iOS: se il click programmatico non scarica,
      // apre il blob in una nuova tab così l'utente può salvarlo manualmente
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 5000);

      toast.dismiss(loadingToastId);
      toast.success(`Backup scaricato (${(blob.size / 1024).toFixed(1)} KB)`);
    } catch (e) {
      toast.dismiss(loadingToastId);
      console.error('Backup download error:', e);
      const msg = e.response?.status === 401 ? 'Sessione scaduta — rifai login'
        : e.response?.status === 403 ? 'Non autorizzato — solo super admin'
        : e.code === 'ECONNABORTED' ? 'Timeout: il backup è troppo grande, riprova'
        : 'Errore download backup: ' + (e.message || 'sconosciuto');
      toast.error(msg, { duration: 6000 });
    }
  };

  const handleDownloadInternalBackup = async (backupId) => {
    const loadingToastId = toast.loading('Download backup automatico...');
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${BACKEND_URL}/api/admin/backup/${backupId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 300000,
      });
      if (!r.data || r.data.size === 0) throw new Error('Backup vuoto');
      const blob = new Blob([r.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ddtmanager_backup_${backupId.slice(0, 8)}.json`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      toast.dismiss(loadingToastId);
      toast.success(`Backup scaricato (${(blob.size / 1024).toFixed(1)} KB)`);
    } catch (e) {
      toast.dismiss(loadingToastId);
      console.error('Backup download error:', e);
      toast.error('Errore download: ' + (e.message || 'sconosciuto'), { duration: 6000 });
    }
  };

  const handleRestoreFile = (e) => {
    setRestoreFile(e.target.files[0]);
  };

  const handleRestoreConfirm = async (e) => {
    e.preventDefault();
    if (!restoreFile) { toast.error('Seleziona un file'); return; }
    if (restoreConfirm !== 'RIPRISTINA') { toast.error('Devi scrivere RIPRISTINA'); return; }
    setSaving(true);
    try {
      const text = await restoreFile.text();
      const snapshot = JSON.parse(text);
      const token = localStorage.getItem('token');
      const r = await axios.post(`${BACKEND_URL}/api/admin/backup/restore`,
        { snapshot, confirm: restoreConfirm },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Ripristino OK: ${JSON.stringify(r.data.restored)}`);
      setRestoreDialogOpen(false);
      setRestoreFile(null);
      setRestoreConfirm('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel ripristino');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    if (!companyForm.name) {
      toast.error('Il nome azienda è obbligatorio');
      return;
    }
    if (!companyForm.username || companyForm.username.length < 3) {
      toast.error('Il nome utente deve essere di almeno 3 caratteri');
      return;
    }
    if (!companyForm.password || companyForm.password.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }
    
    setSaving(true);
    try {
      await adminApi.createCompany({
        ...companyForm,
        username: companyForm.username.trim().toLowerCase(),
      });
      toast.success(`Azienda "${companyForm.name}" creata. Username: ${companyForm.username}`);
      setCompanyDialogOpen(false);
      setCompanyForm({ name: '', username: '', password: '', email: '', piva: '', address: '', city: '', cap: '', phone: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setSaving(false);
    }
  };

  const openCompanyPasswordDialog = (company) => {
    setSelectedCompany(company);
    setCompanyNewPassword('');
    setCompanyPasswordDialog(true);
  };

  const openCompanyUsernameDialog = (company) => {
    setSelectedCompany(company);
    setCompanyNewUsername(company.username || '');
    setCompanyUsernameDialog(true);
  };

  const handleCompanyUsernameChange = async (e) => {
    e.preventDefault();
    const u = (companyNewUsername || '').trim().toLowerCase();
    if (u.length < 3) { toast.error('Min 3 caratteri'); return; }
    setSaving(true);
    try {
      await adminApi.changeCompanyUsername(selectedCompany.id, u);
      toast.success(`Username aggiornato: ${u}`);
      setCompanyUsernameDialog(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally {
      setSaving(false);
    }
  };

  // ====== SLAVE (operatore) account management ======
  const openSlaveDialog = (company) => {
    setSelectedCompany(company);
    setSlaveUsername(company.slave_username || '');
    setSlavePassword('');
    setSlaveDialog(true);
  };

  const handleSaveSlave = async (e) => {
    e.preventDefault();
    const u = (slaveUsername || '').trim().toLowerCase();
    if (u && u.length < 3) { toast.error('Username operatore: min 3 caratteri'); return; }
    if (u && !selectedCompany.slave_username && !slavePassword) {
      toast.error('Imposta una password per il nuovo operatore'); return;
    }
    if (slavePassword && slavePassword.length < 6) {
      toast.error('Password operatore: min 6 caratteri'); return;
    }
    setSaving(true);
    try {
      await adminApi.setSlaveCredentials(selectedCompany.id, u || null, slavePassword || null);
      toast.success(u ? `Operatore aggiornato: ${u}` : 'Account operatore rimosso');
      setSlaveDialog(false);
      setSlaveUsername('');
      setSlavePassword('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveSlave = async () => {
    if (!window.confirm(`Rimuovere l'account operatore di "${selectedCompany.name}"? Non potrà più accedere.`)) return;
    setSaving(true);
    try {
      await adminApi.setSlaveCredentials(selectedCompany.id, null, null);
      toast.success('Account operatore rimosso');
      setSlaveDialog(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally {
      setSaving(false);
    }
  };

  // ====== SUBSCRIPTION RENEW ======
  const openRenewDialog = (company) => {
    setSelectedCompany(company);
    setRenewDays(365);
    setRenewFromToday(false);
    setRenewDialog(true);
  };

  const handleRenew = async (e) => {
    e.preventDefault();
    if (!renewDays || renewDays <= 0) { toast.error('Inserisci un numero di giorni valido'); return; }
    setSaving(true);
    try {
      const r = await adminApi.renewSubscription(selectedCompany.id, parseInt(renewDays), renewFromToday);
      const newDate = new Date(r.data.subscription_expires).toLocaleDateString('it-IT');
      toast.success(`Abbonamento rinnovato. Nuova scadenza: ${newDate}`);
      setRenewDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally {
      setSaving(false);
    }
  };

  // Helper: formatta scadenza con colore in base ai giorni rimanenti
  const renderExpiryCell = (company) => {
    if (!company.subscription_expires) return <span className="text-muted-foreground/40">—</span>;
    const exp = new Date(company.subscription_expires);
    const daysLeft = Math.ceil((exp.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    let color = 'text-emerald-700 bg-emerald-50 border-emerald-200';
    let label = `${daysLeft}g`;
    if (daysLeft <= 0) { color = 'text-red-900 bg-red-100 border-red-400'; label = 'SCADUTO'; }
    else if (daysLeft <= 7) color = 'text-orange-900 bg-orange-50 border-orange-400';
    else if (daysLeft <= 30) color = 'text-amber-900 bg-amber-50 border-amber-300';
    return (
      <div className="text-[10px] space-y-0.5">
        <span className={`inline-block px-1.5 py-0.5 border rounded-sm font-semibold ${color}`}>{label}</span>
        <div className="text-muted-foreground font-['JetBrains_Mono']">{exp.toLocaleDateString('it-IT')}</div>
      </div>
    );
  };

  const handleCompanyPasswordChange = async (e) => {
    e.preventDefault();
    if (companyNewPassword.length < 6) { toast.error('Min 6 caratteri'); return; }
    setSaving(true);
    try {
      await adminApi.changeCompanyPassword(selectedCompany.id, companyNewPassword);
      toast.success(`Password aggiornata per ${selectedCompany.name}`);
      setCompanyPasswordDialog(false);
    } catch (e) {
      toast.error('Errore');
    } finally {
      setSaving(false);
    }
  };

  // Genera password casuale sicura + salva hashed + ritorna in chiaro una-sola-volta.
  // target: 'master' | 'slave'
  const handleRandomResetPassword = async (company, target = 'master') => {
    if (!company) return;
    if (target === 'slave' && !company.slave_username) {
      toast.error('Configura prima un account operatore per questa azienda');
      return;
    }
    setRandomLoading(true);
    try {
      const r = await adminApi.resetCompanyPasswordRandom(company.id, target);
      setRevealedPwd({
        company_name: company.name,
        target,
        username: r.data?.username,
        temporary_password: r.data?.temporary_password,
      });
      setRevealDialog(true);
      // chiude gli altri dialog di modifica se aperti
      setCompanyPasswordDialog(false);
      setSlaveDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella generazione della password');
    } finally {
      setRandomLoading(false);
    }
  };

  const copyRevealedToClipboard = async () => {
    if (!revealedPwd?.temporary_password) return;
    try {
      await navigator.clipboard.writeText(revealedPwd.temporary_password);
      toast.success('Password copiata negli appunti');
    } catch (_) {
      toast.error('Impossibile copiare — selezionala manualmente');
    }
  };

  const openDeleteCompanyDialog = (company) => {
    setSelectedCompany(company);
    setDeleteCompanyConfirm('');
    setDeleteCompanyDialog(true);
  };

  const handleDeleteCompany = async (e) => {
    e.preventDefault();
    if (deleteCompanyConfirm !== selectedCompany?.name) {
      toast.error(`Scrivi esattamente: ${selectedCompany?.name}`);
      return;
    }
    setSaving(true);
    try {
      await adminApi.deleteCompany(selectedCompany.id, true);
      toast.success(`Azienda eliminata definitivamente`);
      setDeleteCompanyDialog(false);
      fetchData();
    } catch (e) {
      toast.error('Errore eliminazione');
    } finally {
      setSaving(false);
    }
  };

  const handleMyPasswordChange = async (e) => {
    e.preventDefault();
    if (myNewPassword.length < 6) { toast.error('Min 6 caratteri'); return; }
    setSaving(true);
    try {
      await adminApi.changeMyPassword(myNewPassword);
      toast.success('Password Super Admin aggiornata');
      setMyPasswordDialog(false);
      setMyNewPassword('');
    } catch (e) {
      toast.error('Errore');
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefaultCausale = async (company) => {
    const value = window.prompt(`Causale trasporto predefinita per "${company.name}" (vuoto = Vendita):`, company.default_causale || '');
    if (value === null) return;
    try {
      await adminApi.updateCompany(company.id, { default_causale: value.trim() });
      toast.success(value.trim() ? `Causale predefinita: ${value.trim()}` : 'Causale predefinita ripristinata a Vendita');
      fetchData();
    } catch (e) {
      toast.error('Errore');
    }
  };

  const handleToggleEinvoice = async (company) => {
    const enable = company.einvoice_access_status?.status !== 'active';
    if (!window.confirm(`${enable ? 'Attivare' : 'Disattivare'} la fatturazione elettronica per "${company.name}"?`)) return;
    try {
      await adminApi.setEinvoiceAccess(company.id, enable);
      toast.success(enable ? 'Fatturazione elettronica attivata' : 'Fatturazione elettronica disattivata');
      fetchData();
    } catch (e) {
      toast.error('Errore');
    }
  };

  const handleToggleCompanyActive = async (company) => {
    const action = company.is_active ? 'sospendere' : 'riattivare';
    if (!window.confirm(`Sei sicuro di voler ${action} l'azienda "${company.name}"?`)) return;
    try {
      const r = await adminApi.toggleCompanyActive(company.id);
      toast.success(r.data.is_active ? 'Azienda riattivata' : 'Azienda sospesa');
      fetchData();
    } catch (e) {
      toast.error('Errore');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!userForm.email || !userForm.password || !userForm.company_id) {
      toast.error('Email, password e azienda sono obbligatori');
      return;
    }
    
    setSaving(true);
    try {
      await adminApi.createUser(userForm);
      toast.success('Utente creato');
      setUserDialogOpen(false);
      setUserForm({ email: '', full_name: '', password: '', company_id: '', role: 'company_user' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleUser = async (userId) => {
    try {
      const result = await adminApi.toggleUserActive(userId);
      toast.success(result.data.is_active ? 'Utente attivato' : 'Utente disattivato');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'operazione');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }
    
    try {
      await adminApi.resetPassword(selectedUser.id, newPassword);
      toast.success('Password resettata');
      setPasswordDialogOpen(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      toast.error('Errore nel reset password');
    }
  };

  const handleToggleCompany = async (companyId) => {
    try {
      await adminApi.deleteCompany(companyId);
      toast.success('Azienda disattivata');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'operazione');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary rounded-sm">
            <Shield className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight font-['Manrope']">Pannello Amministrazione</h1>
          <p className="text-muted-foreground text-sm">Gestione globale del sistema DDT Manager</p>
        </div>
        </div>
        <Button data-testid="change-my-password-btn" variant="outline" size="sm" onClick={() => setMyPasswordDialog(true)}>
          <Key className="w-4 h-4 mr-2" /> Cambia mia password
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-swiss">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Building2 className="w-4 h-4" /> Aziende
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-['JetBrains_Mono']">
              {stats?.active_companies} / {stats?.total_companies}
            </div>
            <p className="text-xs text-muted-foreground">attive / totali</p>
          </CardContent>
        </Card>

        <Card className="card-swiss">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Users className="w-4 h-4" /> Utenti
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-['JetBrains_Mono']">
              {stats?.active_users} / {stats?.total_users}
            </div>
            <p className="text-xs text-muted-foreground">attivi / totali</p>
          </CardContent>
        </Card>

        <Card className="card-swiss">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <FileText className="w-4 h-4" /> DDT Totali
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-['JetBrains_Mono']">
              {stats?.total_ddt}
            </div>
            <p className="text-xs text-muted-foreground">documenti emessi</p>
          </CardContent>
        </Card>

        <Card className="card-swiss bg-green-50/50 border-green-200/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-green-700 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Sistema
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-['JetBrains_Mono'] text-green-700">
              Attivo
            </div>
            <p className="text-xs text-green-600">tutti i servizi operativi</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="companies">
        <TabsList>
          <TabsTrigger value="companies" data-testid="tab-companies">Aziende</TabsTrigger>
          <TabsTrigger value="backup" data-testid="tab-backup">Backup</TabsTrigger>
        </TabsList>

        {/* Companies Tab */}
        <TabsContent value="companies" className="space-y-4">
          <div className="flex justify-end">
            <Button data-testid="new-company-btn" onClick={() => setCompanyDialogOpen(true)} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              NUOVA AZIENDA
            </Button>
          </div>

          <Card className="card-swiss">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground h-10 px-4">Azienda</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground h-10 px-4">Nome utente</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground h-10 px-4">Operatore</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground h-10 px-4">Scadenza</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground h-10 px-4">P.IVA</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground h-10 px-4">DDT</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground h-10 px-4">Stato</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground h-10 px-4 text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats?.companies?.map((company) => (
                    <TableRow key={company.id} className="hover:bg-muted/30">
                      <TableCell className="px-4 py-3">
                        <div>
                          <p className="font-medium">{company.name}</p>
                          <p className="text-xs text-muted-foreground">{company.email || '-'}</p>
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-3 font-['JetBrains_Mono'] text-xs font-medium">
                        {company.username || <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="px-4 py-3 font-['JetBrains_Mono'] text-xs">
                        {company.slave_username ? (
                          <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700 bg-emerald-50">
                            <UserCog className="w-3 h-3 mr-1" /> {company.slave_username}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-[10px]">—</span>
                        )}
                      </TableCell>
                      <TableCell data-testid={`expiry-${company.id}`} className="px-4 py-3">
                        {renderExpiryCell(company)}
                      </TableCell>
                      <TableCell className="px-4 py-3 font-['JetBrains_Mono'] text-xs">
                        {company.piva || '-'}
                      </TableCell>
                      <TableCell className="px-4 py-3 font-['JetBrains_Mono']">
                        {company.ddt_count}
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <Badge variant={company.is_active ? "default" : "secondary"}>
                          {company.is_active ? 'Attiva' : 'Disattivata'}
                        </Badge>
                        <EinvoiceAccessBadge status={company.einvoice_access_status} testId={`einvoice-access-${company.id}`} />
                      </TableCell>
                      <TableCell className="px-4 py-3 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            data-testid={`default-causale-${company.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSetDefaultCausale(company)}
                            title={`Causale predefinita: ${company.default_causale || 'Vendita'}`}
                            className={company.default_causale ? 'text-blue-700' : 'text-muted-foreground'}
                          >
                            <Truck className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`toggle-einvoice-${company.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleEinvoice(company)}
                            title={company.einvoice_access_status?.status === 'active' ? 'Disattiva Fatture' : 'Attiva Fatture (a pagamento)'}
                            className={company.einvoice_access_status?.status === 'active' ? 'text-emerald-700' : 'text-muted-foreground'}
                          >
                            <FileCheck2 className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`toggle-active-${company.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleCompanyActive(company)}
                            title={company.is_active ? 'Sospendi' : 'Riattiva'}
                            className={company.is_active ? 'text-amber-600' : 'text-green-600'}
                          >
                            {company.is_active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                          </Button>
                          <Button
                            data-testid={`change-username-${company.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => openCompanyUsernameDialog(company)}
                            title="Cambia nome utente"
                          >
                            <UserPlus className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`change-pwd-${company.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => openCompanyPasswordDialog(company)}
                            title="Cambia password (imposta manualmente)"
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`random-pwd-${company.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRandomResetPassword(company, 'master')}
                            disabled={randomLoading}
                            title="Genera password casuale (mostrata una sola volta)"
                            className="text-violet-700 hover:text-violet-800 hover:bg-violet-50"
                          >
                            <Dices className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`manage-slave-${company.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => openSlaveDialog(company)}
                            title="Gestisci account operatore"
                            className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                          >
                            <UserCog className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`renew-sub-${company.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => openRenewDialog(company)}
                            title="Rinnova abbonamento"
                            className="text-blue-700 hover:text-blue-800 hover:bg-blue-50"
                          >
                            <CalendarClock className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`delete-company-${company.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteCompanyDialog(company)}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            title="Elimina definitivamente"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {(!stats?.companies || stats.companies.length === 0) && (
                <div className="text-center py-12 text-muted-foreground">
                  <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nessuna azienda registrata</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab REMOVED — companies have built-in credentials now */}
        <TabsContent value="users" className="space-y-4 hidden">
          <div className="flex justify-end">
            <Button onClick={() => setUserDialogOpen(true)} className="btn-primary">
              <UserPlus className="w-4 h-4 mr-2" />
              NUOVO UTENTE
            </Button>
          </div>

          <Card className="card-swiss">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground h-10 px-4">Utente</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground h-10 px-4">Azienda</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground h-10 px-4">Ruolo</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground h-10 px-4">Stato</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground h-10 px-4">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} className="hover:bg-muted/30">
                      <TableCell className="px-4 py-3">
                        <div>
                          <p className="font-medium">{user.full_name || '-'}</p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        {user.company_name || '-'}
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <Badge variant="outline">
                          {user.role === 'company_admin' ? 'Admin' : 'Utente'}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <Badge variant={user.is_active ? "default" : "secondary"}>
                          {user.is_active ? 'Attivo' : 'Disattivato'}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleToggleUser(user.id)}
                            className="h-8 w-8"
                            title={user.is_active ? 'Disattiva' : 'Attiva'}
                          >
                            {user.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { setSelectedUser(user); setPasswordDialogOpen(true); }}
                            className="h-8 w-8"
                            title="Reset password"
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {users.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun utente registrato</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Backup Tab */}
        <TabsContent value="backup" className="space-y-4">
          <Card className="card-swiss">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  Backup & Ripristino
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Scarica un backup completo dei dati (aziende, utenti, clienti, prodotti, DDT, pagamenti).
                  Backup giornaliero automatico alle 03:00 UTC con rotazione 7 giorni.
                </p>
              </div>
              <div className="flex gap-2">
                <Button data-testid="backup-download-btn" onClick={handleDownloadBackup} className="btn-primary">
                  <Download className="w-4 h-4 mr-2" />Scarica Backup Adesso
                </Button>
                <Button data-testid="backup-restore-btn" variant="outline" onClick={() => setRestoreDialogOpen(true)}>
                  <Upload className="w-4 h-4 mr-2" />Ripristina
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Backup automatici (ultimi 7 giorni)</h3>
              {backups.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">Nessun backup automatico ancora generato. Il primo verrà creato alle 03:00 UTC.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="table-header-swiss">Data e ora</TableHead>
                      <TableHead className="table-header-swiss">Tipo</TableHead>
                      <TableHead className="table-header-swiss">Statistiche</TableHead>
                      <TableHead className="table-header-swiss w-[120px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {backups.map((b) => (
                      <TableRow key={b.id}>
                        <TableCell className="table-cell-swiss font-['JetBrains_Mono'] text-xs">
                          {new Date(b.created_at).toLocaleString('it-IT')}
                        </TableCell>
                        <TableCell className="table-cell-swiss">
                          <Badge variant={b.label === 'auto-daily' ? 'default' : 'outline'}>{b.label}</Badge>
                        </TableCell>
                        <TableCell className="table-cell-swiss text-xs text-muted-foreground">
                          {b.stats ? Object.entries(b.stats).map(([k, v]) => `${k}:${v}`).join(' · ') : '-'}
                        </TableCell>
                        <TableCell className="table-cell-swiss">
                          <Button data-testid={`download-backup-${b.id}`} variant="ghost" size="sm" onClick={() => handleDownloadInternalBackup(b.id)}>
                            <Download className="w-4 h-4 mr-1" />Scarica
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card className="card-swiss bg-amber-50/40 border-amber-200/50">
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold mb-1">Suggerimento di sicurezza</p>
                  <p className="text-muted-foreground">
                    Scarica un backup completo prima di qualsiasi modifica importante (creazione massiva, import CSV, redeploy).
                    Conserva i file `.json` su un Cloud (Drive/Dropbox) o disco esterno.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Restore Dialog */}
      <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" /> Ripristina Backup
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRestoreConfirm} className="space-y-3 py-2">
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs">
              <p className="font-semibold mb-1">⚠️ Attenzione</p>
              <p>Il ripristino sostituirà <b>tutti i dati attuali</b> con quelli del backup. Sarà creato un backup di sicurezza pre-ripristino prima di procedere.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">File backup (.json)</Label>
              <Input data-testid="restore-file-input" type="file" accept=".json" onChange={handleRestoreFile} required />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Per confermare scrivi: <b>RIPRISTINA</b></Label>
              <Input data-testid="restore-confirm-input" value={restoreConfirm} onChange={(e) => setRestoreConfirm(e.target.value)} placeholder="RIPRISTINA" required />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRestoreDialogOpen(false)}>Annulla</Button>
              <Button type="submit" disabled={saving || restoreConfirm !== 'RIPRISTINA'} className="bg-destructive hover:bg-destructive/90 text-destructive-foreground">
                {saving ? 'Ripristino...' : 'Conferma Ripristino'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Company Dialog */}
      <Dialog open={companyDialogOpen} onOpenChange={setCompanyDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] font-semibold">Nuova Azienda</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCompany}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Nome Azienda *</Label>
                <Input
                  data-testid="new-company-name"
                  value={companyForm.name}
                  onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                  placeholder="Es. Azienda S.r.l."
                />
              </div>
              <div className="grid grid-cols-2 gap-4 bg-emerald-50/40 border border-emerald-200/40 rounded-sm p-3">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Nome utente *</Label>
                  <Input
                    data-testid="new-company-username"
                    value={companyForm.username}
                    onChange={(e) => setCompanyForm({ ...companyForm, username: e.target.value.toLowerCase().replace(/\s+/g, '') })}
                    placeholder="es. mio_negozio"
                    required
                  />
                  <p className="text-[10px] text-muted-foreground">Sarà usato per il login. Solo minuscole, niente spazi.</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Password *</Label>
                  <Input
                    data-testid="new-company-password"
                    type="text"
                    value={companyForm.password}
                    onChange={(e) => setCompanyForm({ ...companyForm, password: e.target.value })}
                    placeholder="Min 6 caratteri"
                    required
                  />
                  <p className="text-[10px] text-muted-foreground">Comunicala al cliente per primo accesso.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                  <Input
                    type="email"
                    value={companyForm.email}
                    onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })}
                    placeholder="info@azienda.it"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">P.IVA</Label>
                  <Input
                    value={companyForm.piva}
                    onChange={(e) => setCompanyForm({ ...companyForm, piva: e.target.value })}
                    placeholder="IT00000000000"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Indirizzo</Label>
                <Input
                  value={companyForm.address}
                  onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })}
                  placeholder="Via Roma, 1"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Città</Label>
                  <Input
                    value={companyForm.city}
                    onChange={(e) => setCompanyForm({ ...companyForm, city: e.target.value })}
                    placeholder="Milano"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">CAP</Label>
                  <Input
                    value={companyForm.cap}
                    onChange={(e) => setCompanyForm({ ...companyForm, cap: e.target.value })}
                    placeholder="20100"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Telefono</Label>
                  <Input
                    value={companyForm.phone}
                    onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                    placeholder="+39..."
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCompanyDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={saving} className="btn-primary">
                {saving ? 'Creazione...' : 'CREA AZIENDA'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] font-semibold">Nuovo Utente</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Azienda *</Label>
                <Select
                  value={userForm.company_id}
                  onValueChange={(value) => setUserForm({ ...userForm, company_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona azienda..." />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.filter(c => c.is_active).map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email *</Label>
                  <Input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    placeholder="utente@azienda.it"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Nome Completo</Label>
                  <Input
                    value={userForm.full_name}
                    onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })}
                    placeholder="Mario Rossi"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Password Iniziale *</Label>
                  <Input
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    placeholder="Min. 6 caratteri"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Ruolo</Label>
                  <Select
                    value={userForm.role}
                    onValueChange={(value) => setUserForm({ ...userForm, role: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="company_user">Utente</SelectItem>
                      <SelectItem value="company_admin">Admin Azienda</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setUserDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={saving} className="btn-primary">
                {saving ? 'Creazione...' : 'CREA UTENTE'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] font-semibold">Reset Password</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Reset password per: <strong>{selectedUser?.email}</strong>
            </p>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Nuova Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min. 6 caratteri"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setPasswordDialogOpen(false); setNewPassword(''); }}>
              Annulla
            </Button>
            <Button onClick={handleResetPassword} className="btn-primary">
              RESET PASSWORD
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Company Username Dialog */}
      <Dialog open={companyUsernameDialog} onOpenChange={setCompanyUsernameDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-semibold">Cambia nome utente — {selectedCompany?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCompanyUsernameChange} className="space-y-3 py-2">
            <p className="text-xs text-muted-foreground">
              Username attuale: <span className="font-['JetBrains_Mono'] font-medium">{selectedCompany?.username || 'N/D'}</span>
            </p>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Nuovo nome utente *</Label>
              <Input
                data-testid="new-company-username-input"
                value={companyNewUsername}
                onChange={(e) => setCompanyNewUsername(e.target.value.toLowerCase().replace(/\s+/g, ''))}
                placeholder="es. mio_negozio"
                required
              />
              <p className="text-[10px] text-muted-foreground mt-1">Min 3 caratteri, solo minuscole, niente spazi.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCompanyUsernameDialog(false)}>Annulla</Button>
              <Button data-testid="save-company-username-btn" type="submit" disabled={saving} className="btn-primary">
                {saving ? 'Salvataggio...' : 'Aggiorna Username'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Company Password Dialog */}
      <Dialog open={companyPasswordDialog} onOpenChange={setCompanyPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-semibold">Cambia password — {selectedCompany?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCompanyPasswordChange} className="space-y-3 py-2">
            <p className="text-xs text-muted-foreground">
              Username: <span className="font-['JetBrains_Mono'] font-medium">{selectedCompany?.username || 'N/D'}</span>
            </p>
            <div className="rounded-md border border-blue-200 bg-blue-50 p-2.5 text-[11px] text-blue-900 flex gap-2">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                Le password sono salvate come <b>hash bcrypt</b> (non recuperabili).
                Non è possibile visualizzare quella attuale — puoi impostarne una nuova o generarne una casuale.
              </span>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Nuova password *</Label>
              <Input
                data-testid="new-company-pwd-input"
                value={companyNewPassword}
                onChange={(e) => setCompanyNewPassword(e.target.value)}
                placeholder="Min 6 caratteri"
                required
              />
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleRandomResetPassword(selectedCompany, 'master')}
                disabled={randomLoading}
                data-testid="generate-random-pwd-btn"
                className="border-violet-300 text-violet-700 hover:bg-violet-50"
              >
                <Dices className="w-4 h-4 mr-1.5" />
                Genera casuale
              </Button>
              <Button type="button" variant="outline" onClick={() => setCompanyPasswordDialog(false)}>Annulla</Button>
              <Button data-testid="save-company-pwd-btn" type="submit" disabled={saving} className="btn-primary">
                {saving ? 'Salvataggio...' : 'Aggiorna Password'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Company Dialog */}
      <Dialog open={deleteCompanyDialog} onOpenChange={setDeleteCompanyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" /> Elimina Azienda
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleDeleteCompany} className="space-y-3 py-2">
            <div className="bg-destructive/10 border border-destructive/30 rounded-sm p-3 text-xs">
              <p className="font-semibold mb-1">⚠️ Attenzione, operazione irreversibile</p>
              <p>Saranno eliminati definitivamente: l'azienda <b>{selectedCompany?.name}</b>, tutti i suoi clienti, prodotti, DDT e pagamenti. Verrà creato uno snapshot interno prima dell'eliminazione (ripristinabile dal Backup).</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Per confermare, scrivi esattamente: <b className="text-destructive">{selectedCompany?.name}</b>
              </Label>
              <Input
                data-testid="delete-company-confirm-input"
                value={deleteCompanyConfirm}
                onChange={(e) => setDeleteCompanyConfirm(e.target.value)}
                placeholder={selectedCompany?.name}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDeleteCompanyDialog(false)}>Annulla</Button>
              <Button
                data-testid="confirm-delete-company-btn"
                type="submit"
                disabled={saving || deleteCompanyConfirm !== selectedCompany?.name}
                className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              >
                {saving ? 'Eliminazione...' : 'Elimina Definitivamente'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change My Password (Super Admin) Dialog */}
      <Dialog open={myPasswordDialog} onOpenChange={setMyPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Key className="w-5 h-5" /> Cambia password Super Admin</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleMyPasswordChange} className="space-y-3 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Nuova password *</Label>
              <Input
                data-testid="my-new-pwd-input"
                value={myNewPassword}
                onChange={(e) => setMyNewPassword(e.target.value)}
                placeholder="Min 6 caratteri"
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMyPasswordDialog(false)}>Annulla</Button>
              <Button data-testid="save-my-pwd-btn" type="submit" disabled={saving} className="btn-primary">
                {saving ? 'Salvataggio...' : 'Aggiorna'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Subscription Renew Dialog */}
      <Dialog open={renewDialog} onOpenChange={setRenewDialog}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-['Manrope'] font-semibold">
              <CalendarClock className="w-5 h-5 text-blue-700" />
              Rinnova abbonamento — {selectedCompany?.name}
            </DialogTitle>
            <DialogDescription>
              Estende la scadenza dell'azienda di N giorni.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRenew} className="space-y-3 py-2">
            {selectedCompany?.subscription_expires && (
              <div className="bg-muted/40 border border-border rounded-sm p-3 text-xs space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Scadenza attuale</span>
                  <span className="font-['JetBrains_Mono'] font-semibold">
                    {new Date(selectedCompany.subscription_expires).toLocaleDateString('it-IT')}
                  </span>
                </div>
                {(() => {
                  const d = Math.ceil((new Date(selectedCompany.subscription_expires).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                  return (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Giorni rimanenti</span>
                      <span className={`font-['JetBrains_Mono'] font-semibold ${d <= 0 ? 'text-red-700' : d <= 30 ? 'text-amber-700' : 'text-emerald-700'}`}>
                        {d <= 0 ? 'SCADUTO' : `${d} giorni`}
                      </span>
                    </div>
                  );
                })()}
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              {[30, 90, 365].map(d => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setRenewDays(d)}
                  data-testid={`renew-quick-${d}`}
                  className={`px-2 py-2 rounded-sm border text-xs font-medium transition ${
                    parseInt(renewDays) === d ? 'bg-foreground text-background border-foreground' : 'bg-background hover:bg-muted border-border'
                  }`}
                >
                  {d === 365 ? '+ 1 anno' : `+ ${d} giorni`}
                </button>
              ))}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Giorni di rinnovo *</Label>
              <Input
                data-testid="renew-days-input"
                type="number" min="1" max="3650"
                value={renewDays}
                onChange={(e) => setRenewDays(e.target.value)}
                required
              />
            </div>
            <label className="flex items-start gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                data-testid="renew-from-today"
                checked={renewFromToday}
                onChange={(e) => setRenewFromToday(e.target.checked)}
                className="mt-0.5"
              />
              <div>
                <div className="font-medium">Riparti da oggi</div>
                <div className="text-muted-foreground text-[10px]">
                  Se attivo, la nuova scadenza = oggi + giorni. Altrimenti estende la scadenza attuale (se nel futuro).
                </div>
              </div>
            </label>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRenewDialog(false)}>Annulla</Button>
              <Button
                data-testid="save-renew-btn"
                type="submit"
                disabled={saving}
                className="btn-primary bg-blue-700 hover:bg-blue-800"
              >
                <CalendarClock className="w-4 h-4 mr-2" />
                {saving ? 'Rinnovo...' : 'RINNOVA ABBONAMENTO'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Slave (Operatore) Credentials Dialog */}
      <Dialog open={slaveDialog} onOpenChange={setSlaveDialog}>        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-['Manrope'] font-semibold">
              <UserCog className="w-5 h-5 text-emerald-700" />
              Account Operatore — {selectedCompany?.name}
            </DialogTitle>
            <DialogDescription>
              Gestisci le credenziali dell'account operatore (slave) di questa azienda.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveSlave} className="space-y-3 py-2">
            <div className="bg-emerald-50/60 border border-emerald-200 rounded-sm p-3 text-xs space-y-1">
              <p className="font-semibold text-emerald-800">Cos'è l'account operatore?</p>
              <p className="text-emerald-900/80">
                Un secondo accesso (es. dipendente) con permessi <b>ridotti</b>: può creare/modificare DDT e preventivi,
                vedere clienti e prodotti, ma <b>non può</b> eliminare DDT/clienti né registrare/cancellare pagamenti.
              </p>
            </div>
            <div className="text-xs text-muted-foreground">
              Titolare: <span className="font-['JetBrains_Mono'] font-medium text-foreground">{selectedCompany?.username || '—'}</span>
              {selectedCompany?.slave_username && (
                <> · Operatore attuale: <span className="font-['JetBrains_Mono'] font-medium text-foreground">{selectedCompany.slave_username}</span></>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Username operatore</Label>
              <Input
                data-testid="slave-username-input"
                value={slaveUsername}
                onChange={(e) => setSlaveUsername(e.target.value.toLowerCase().replace(/\s+/g, ''))}
                placeholder="es. operatore_mario"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Lascia vuoto e premi "Rimuovi" per eliminare l'account operatore. Min 3 caratteri, diverso dal titolare.
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Password {selectedCompany?.slave_username ? '(lascia vuoto per non cambiarla)' : '*'}
              </Label>
              <Input
                data-testid="slave-password-input"
                type="text"
                value={slavePassword}
                onChange={(e) => setSlavePassword(e.target.value)}
                placeholder="Min 6 caratteri"
              />
            </div>
            <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
              {selectedCompany?.slave_username && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleRemoveSlave}
                  disabled={saving}
                  className="mr-auto text-destructive hover:bg-destructive/10 border-destructive/30"
                  data-testid="remove-slave-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Rimuovi operatore
                </Button>
              )}
              {selectedCompany?.slave_username && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleRandomResetPassword(selectedCompany, 'slave')}
                  disabled={randomLoading}
                  className="border-violet-300 text-violet-700 hover:bg-violet-50"
                  data-testid="generate-random-slave-pwd-btn"
                >
                  <Dices className="w-4 h-4 mr-1.5" /> Genera casuale
                </Button>
              )}
              <Button type="button" variant="outline" onClick={() => setSlaveDialog(false)}>Annulla</Button>
              <Button
                data-testid="save-slave-btn"
                type="submit"
                disabled={saving || !slaveUsername}
                className="btn-primary bg-emerald-700 hover:bg-emerald-800"
              >
                {saving ? 'Salvataggio...' : (selectedCompany?.slave_username ? 'Aggiorna operatore' : 'Crea operatore')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* One-shot Password Reveal Dialog */}
      <Dialog open={revealDialog} onOpenChange={(o) => { setRevealDialog(o); if (!o) setRevealedPwd(null); }}>
        <DialogContent className="sm:max-w-[520px]" data-testid="reveal-pwd-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Dices className="w-5 h-5 text-violet-600" />
              Password generata
            </DialogTitle>
            <DialogDescription>
              Nuova password per <b>{revealedPwd?.company_name}</b>
              {revealedPwd?.target === 'slave' && <> — account <b>Operatore</b></>}
              . Copia subito la password: <b>non sarà più visibile</b> dopo aver chiuso questa finestra.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Username</Label>
              <div
                data-testid="revealed-username"
                className="mt-1 font-['JetBrains_Mono'] text-sm bg-muted rounded px-3 py-2 select-all"
              >
                {revealedPwd?.username || '—'}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Password temporanea</Label>
              <div className="flex gap-2 mt-1">
                <div
                  data-testid="revealed-password"
                  className="flex-1 font-['JetBrains_Mono'] text-lg font-bold tracking-wider bg-violet-50 border-2 border-violet-300 rounded px-3 py-2 select-all"
                >
                  {revealedPwd?.temporary_password || '—'}
                </div>
                <Button
                  type="button"
                  onClick={copyRevealedToClipboard}
                  data-testid="copy-revealed-pwd-btn"
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                >
                  <Copy className="w-4 h-4 mr-1.5" />
                  Copia
                </Button>
              </div>
            </div>
            <div className="rounded-md border border-amber-300 bg-amber-50 p-2.5 text-[11px] text-amber-900 flex gap-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                Trasmetti la password al titolare in modo sicuro (whatsapp/email diretto). Al prossimo accesso l'utente potrà cambiarla dalle Impostazioni.
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="close-reveal-dialog-btn"
              onClick={() => { setRevealDialog(false); setRevealedPwd(null); }}
              className="btn-primary"
            >
              Ho copiato la password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;

